import { AccessTokensData } from '@squareup/dex-store-access-shared-main-use-access-tokens-hook';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { SandboxAuthorization } from '@squareup/dex-types-shared-sandbox-test-account';
import { useApiExplorerContext } from '@squareup/dex-ui-shared-api-explorer-providers';
import { useEffect, useMemo, useRef, useState } from 'react';

const useSupportedEnvironment = (
  isLoading: boolean,
  supportedEnvironments: ApplicationType[],
  accessTokens: AccessTokensData | undefined,
  appId: string | undefined
) => {
  const { accessTokenData, dispatchAccessTokenAction } =
    useApiExplorerContext();

  const isSandboxSupported = supportedEnvironments.includes(
    ApplicationType.Sandbox
  );

  const { token, environment: storedEnvironment } = accessTokenData;

  const isAppIdTokenSet = useRef(false);

  const environment: ApplicationType = useMemo(() => {
    if (!isSandboxSupported) {
      return ApplicationType.Production;
    }
    return storedEnvironment;
  }, [isSandboxSupported, storedEnvironment]);

  useEffect(() => {
    if (!isLoading) {
      // If the stored environment is different from the context environment, update the (context) environment
      // This will only happen when there is an unsupported environment (toggle to supported environment)
      if (storedEnvironment !== environment) {
        dispatchAccessTokenAction({
          type: 'SET_ENVIRONMENT',
          payload: environment,
        });
      }
    }
  }, [dispatchAccessTokenAction, environment, isLoading, storedEnvironment]);

  useEffect(() => {
    // If appId param is passed, we should find an access token for the (production) client with the same id
    // and set it as the token. `appId` is usually accompanied by `env` param which should already by set.
    if (
      !appId ||
      !accessTokens ||
      !(accessTokens.Sandbox || accessTokens.Production) ||
      isAppIdTokenSet.current
    ) {
      return;
    }
    // Ensures that the token is set only once otherwise we interfere with the user's input
    isAppIdTokenSet.current = true;

    if (environment === ApplicationType.Sandbox) {
      const appAccessTokens = accessTokens?.Sandbox;
      const sandboxClient = appAccessTokens?.oauthClients.find(
        (client) => client.production_client === appId
      )?.id;
      // Search for a token in accessTokens first, then authorizations
      // Access tokens are for default/primary accounts and don't expire
      const sandboxToken =
        appAccessTokens?.accessTokens?.find(
          (auth) => auth.client_id === sandboxClient
        )?.token ||
        appAccessTokens?.authorizations?.find(
          (auth) => auth.client === sandboxClient
        )?.access_token;

      if (sandboxToken && token !== sandboxToken) {
        dispatchAccessTokenAction({
          type: 'SET_TOKEN',
          payload: sandboxToken,
        });
      }
    }
    if (environment === ApplicationType.Production) {
      const productionToken = accessTokens?.[environment]?.accessTokens?.find(
        (token) => token.client_id === appId
      );
      if (productionToken) {
        dispatchAccessTokenAction({
          type: 'SET_TOKEN',
          payload: productionToken.token,
        });
      }
    }
  }, [accessTokens, appId, dispatchAccessTokenAction, environment, token]);

  return {
    environment,
  };
};

const useExpiredAuth = (
  token: string,
  accessTokenData: AccessTokensData[keyof AccessTokensData] | undefined,
  isAuthenticated: boolean
) => {
  const [expiredAuth, setExpiredAuth] = useState<{
    name: string;
    auth: SandboxAuthorization;
  }>();

  useEffect(() => {
    const sandboxData =
      accessTokenData as AccessTokensData[ApplicationType.Sandbox];
    if (
      !isAuthenticated ||
      !sandboxData ||
      sandboxData.authorizations?.length === 0
    ) {
      setExpiredAuth(undefined);
      return;
    }
    const sandboxAuth = sandboxData?.authorizations?.find(
      (auth) => auth.access_token === token
    );
    const sandboxMerchant = sandboxData?.merchants?.find(
      (merchant) => sandboxAuth?.sandbox_merchant === merchant.token
    );
    // If the token is expired and the merchant is found, return data
    if (
      sandboxAuth &&
      sandboxAuth.expires_at &&
      new Date(sandboxAuth.expires_at) < new Date() &&
      sandboxMerchant
    ) {
      setExpiredAuth({
        name: sandboxMerchant.nickname,
        auth: sandboxAuth,
      });
      return;
    }
    setExpiredAuth(undefined);
  }, [accessTokenData, isAuthenticated, token]);

  return { expiredAuth, setExpiredAuth };
};

export { useSupportedEnvironment, useExpiredAuth };
